import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Button from "@components/Button";
import Input from "@components/Input";
import Spinner from "@components/Spinner";
import { getDeviceLogs, AdminDeviceLog } from "@services/adminService";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import usePolling from "@src/hooks/usePolling";
import { useDebouncedValue } from "@src/hooks/useDebouncedValue";

// ── Styled ────────────────────────────────────────────────

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterInput = styled(Input)`
  height: 36px;
  width: 200px;
  min-width: 140px;
  padding: 0 12px;
  box-sizing: border-box;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex: 1;
    min-width: 0;
  }
`;

const FilterSelect = styled.select`
  height: 36px;
  border: 1px solid #ebeeef;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  padding: 0 12px;
  color: #494c5b;
  outline: none;
  background: white;
`;

const FilterButton = styled(Button)`
  width: auto;
  min-width: 80px;
`;

const AutoRefreshLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #899093;
  cursor: pointer;
  margin-left: auto;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-left: 0;
  }
`;

const Table = styled.div`
  width: 100%;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  overflow: hidden;
`;

const LogCard = styled.div<{ expanded?: boolean; level: string }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  background: ${({ expanded }) => (expanded ? "#f0f4ff" : "transparent")};
  border-left: 3px solid
    ${({ level }) =>
      level === "fatal"
        ? "#9b1c1c"
        : level === "error"
        ? "#e53e3e"
        : level === "warn"
        ? "#d69e2e"
        : "#cbd5e0"};
  transition: background 0.15s;
  &:hover {
    background: ${({ expanded }) => (expanded ? "#f0f4ff" : "#fafbfc")};
  }
  &:last-child {
    border-bottom: none;
  }
`;

const LevelPill = styled.span<{ level: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: white;
  background: ${({ level }) =>
    level === "fatal"
      ? "#9b1c1c"
      : level === "error"
      ? "#e53e3e"
      : level === "warn"
      ? "#d69e2e"
      : level === "info"
      ? "#4a90d9"
      : "#718096"};
`;

const LogMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TopLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TagText = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: #4a90d9;
  font-weight: 600;
`;

const Message = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #2d3748;
  word-break: break-word;
`;

const Meta = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #899093;
`;

const CountBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: #ebeeef;
  color: #494c5b;
`;

const Details = styled.div`
  padding: 12px 16px 16px 32px;
  background: #fafbfc;
  border-bottom: 1px solid #f0f0f0;
`;

const Pre = styled.pre`
  font-family: monospace;
  font-size: 11px;
  color: #2d3748;
  background: white;
  border: 1px solid #ebeeef;
  border-radius: 6px;
  padding: 10px;
  margin: 6px 0 0 0;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 300px;
  overflow: auto;
`;

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  color: #899093;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  gap: 12px;
`;

// ── Component ─────────────────────────────────────────────

const PAGE_SIZE = 100;

const AdminDeviceLogsTab: React.FC = () => {
  const [logs, setLogs] = useState<AdminDeviceLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [offset, setOffset] = useState(0);

  const [installId, setInstallId] = useState("");
  const [source, setSource] = useState("");
  const [deviceEsn, setDeviceEsn] = useState("");
  const [level, setLevel] = useState<"" | "warn" | "error" | "fatal">("");
  const [tag, setTag] = useState("");
  const [search, setSearch] = useState("");
  // Text filters query once typing pauses, not on every keystroke.
  const qInstallId = useDebouncedValue(installId);
  const qDeviceEsn = useDebouncedValue(deviceEsn);
  const qTag = useDebouncedValue(tag);
  const qSearch = useDebouncedValue(search);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDeviceLogs({
        installId: qInstallId || undefined,
        source: source || undefined,
        deviceEsn: qDeviceEsn || undefined,
        level: level || undefined,
        tag: qTag || undefined,
        search: qSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setLogs(res.logs);
      setTotal(res.total);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to load device logs", e);
    } finally {
      setLoading(false);
    }
  }, [qInstallId, source, qDeviceEsn, level, qTag, qSearch, offset]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Poll every 30s while auto-refresh is on, but only when the tab is visible
  // (a backgrounded tab polling the DB keeps the Neon compute from suspending).
  usePolling(fetchLogs, 30000, autoRefresh);

  const applyFilters = () => {
    setOffset(0);
    fetchLogs();
  };

  const clearFilters = () => {
    setInstallId("");
    setSource("");
    setDeviceEsn("");
    setLevel("");
    setTag("");
    setSearch("");
    setOffset(0);
  };

  const formatTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div>
      <FilterRow>
        <FilterInput
          placeholder="Search message / tag"
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") applyFilters();
          }}
        />
        <FilterInput
          placeholder="Device ESN"
          value={deviceEsn}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeviceEsn(e.target.value)}
        />
        <FilterInput
          placeholder="Install ID"
          value={installId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInstallId(e.target.value)}
        />
        <FilterInput
          placeholder="Tag"
          value={tag}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTag(e.target.value)}
        />
        <FilterSelect value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">All apps</option>
          <option value="greengrowth">GreenGrowth</option>
          <option value="truck2truck">truck2truck</option>
        </FilterSelect>
        <FilterSelect value={level} onChange={(e) => setLevel(e.target.value as any)}>
          <option value="">All levels</option>
          <option value="fatal">Fatal</option>
          <option value="error">Error</option>
          <option value="warn">Warn</option>
        </FilterSelect>
        <FilterButton onClick={applyFilters}>Apply</FilterButton>
        <FilterButton onClick={clearFilters}>Clear</FilterButton>
        <AutoRefreshLabel>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh (10s)
        </AutoRefreshLabel>
      </FilterRow>

      {loading && logs.length === 0 ? (
        <Empty>
          <Spinner />
        </Empty>
      ) : logs.length === 0 ? (
        <Empty>No device logs found.</Empty>
      ) : (
        <Table>
          {logs.map((log) => {
            const expanded = expandedId === log.id;
            return (
              <React.Fragment key={log.id}>
                <LogCard
                  level={log.level}
                  expanded={expanded}
                  onClick={() => setExpandedId(expanded ? null : log.id)}
                >
                  <LogMain>
                    <TopLine>
                      <LevelPill level={log.level}>{log.level}</LevelPill>
                      <TagText>{log.tag}</TagText>
                      {log.count > 1 && <CountBadge>×{log.count}</CountBadge>}
                      {log.deviceEsn && (
                        <Text size={12} color="#899093">
                          ESN: {log.deviceEsn}
                        </Text>
                      )}
                    </TopLine>
                    <Message>{log.message}</Message>
                    <Meta>
                      {log.source && `${log.source} · `}
                      {formatTime(log.lastSeenAt)}
                      {log.appVersion && ` · v${log.appVersion}`}
                      {log.deviceModel && ` · ${log.deviceModel}`}
                      {log.androidVersion && ` · ${log.androidVersion}`}
                    </Meta>
                  </LogMain>
                </LogCard>
                {expanded && (
                  <Details>
                    <Text size={12} color="#899093">
                      Install: <span style={{ fontFamily: "monospace" }}>{log.installId}</span>
                      {" · "}
                      Fingerprint:{" "}
                      <span style={{ fontFamily: "monospace" }}>
                        {log.fingerprint.slice(0, 12)}…
                      </span>
                      {" · "}
                      First seen: {formatTime(log.firstSeenAt)}
                    </Text>
                    {log.stacktrace && (
                      <>
                        <Text size={12} color="#494c5b" style={{ marginTop: 8 }}>
                          Stacktrace
                        </Text>
                        <Pre>{log.stacktrace}</Pre>
                      </>
                    )}
                    {log.metadata && Object.keys(log.metadata).length > 0 && (
                      <>
                        <Text size={12} color="#494c5b" style={{ marginTop: 8 }}>
                          Metadata
                        </Text>
                        <Pre>{JSON.stringify(log.metadata, null, 2)}</Pre>
                      </>
                    )}
                  </Details>
                )}
              </React.Fragment>
            );
          })}
        </Table>
      )}

      {total > PAGE_SIZE && (
        <Pagination>
          <Text size={13} color="#899093">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </Text>
          <div style={{ display: "flex", gap: 8 }}>
            <FilterButton
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Prev
            </FilterButton>
            <FilterButton
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </FilterButton>
          </div>
        </Pagination>
      )}
    </div>
  );
};

export default AdminDeviceLogsTab;
